import LoadingAnimation from "@assets/svg/LoadingAnimation";
import PlusCircleIcon from "@assets/svg/PlusCircleIcon";
import { getInterviewDetails } from "@endpoints/interview.service";
import {
  IGeneralSlotResponse,
  IGetInterviewByIdResponse,
} from "@endpoints/types/interview.type";
import { RootState } from "@store/reducers";
import { EnumUserTypes, userTypes } from "@utils/constants";
import dayjs from "@lib/dayjs";
import { useSnackbar } from "notistack";
import { useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import LatestProposalCard from "./components/LatestProposalCard";
import PastProposalCard from "./components/PastProposalCard";
import { isDeepEqual } from "@utils/helperFunction";
import InviteConfirmationContainer from "./components/InviteConfirmationContainer";
import store from "@store/index";
import SpecificSlotProposal from "./components/SpecificSlotProposal";
import { MeetingModeEnum } from "@type/meeting.type";
import LinkIcon from "@assets/svg/LinkIcon";
import CopyIcon from "@assets/svg/CopyIcon";
import { CopyToClipboard } from "react-copy-to-clipboard";
import ChevronLeftIcon from "@assets/svg/ChevronLeftIcon";

export interface IAvailabilitySlot extends IGeneralSlotResponse {
  isDeclined?: boolean;
  isAccepted?: boolean;
  isDeclinedTemp?: boolean;
  isAcceptedTemp?: boolean;
}

export const MeetingParties = [
  EnumUserTypes.CANDIDATE,
  EnumUserTypes.RECRUITER,
  EnumUserTypes.HIRING_COMPANY,
];

export interface TypeGroupOfProposals {
  createdAt: string;
  slots: IGeneralSlotResponse[];
}

function keyExistInObject({
  object,
  key,
}: {
  object: Record<string, IGeneralSlotResponse[]>;
  key: string;
}) {
  const keyExist = Object.entries(object).find(
    ([itemKey, _]) => Math.abs(dayjs(itemKey).diff(dayjs(key), "seconds")) < 10
  );
  return keyExist ? keyExist[0] : null;
}

export function separatePastAndNewProposals(slots: IGeneralSlotResponse[]) {
  if (!Array.isArray(slots)) return [];
  const separatedSlotObject: Record<string, IGeneralSlotResponse[]> = {};
  for (let slot of slots) {
    const key = slot?.createdAt;
    const updatedSlot = { ...slot, status: getSlotStatus({ slot }) };
    const existingKey = keyExistInObject({
      object: separatedSlotObject,
      key,
    });
    if (existingKey) {
      separatedSlotObject[existingKey].push(updatedSlot);
    } else {
      separatedSlotObject[key] = [updatedSlot];
    }
  }

  const sortedByCreatedAt = Object.entries(separatedSlotObject).sort(
    ([keyA], [keyB]) => dayjs(keyB).valueOf() - dayjs(keyA).valueOf()
  );
  let finalArray = [];
  sortedByCreatedAt?.forEach((item) => {
    finalArray.push({
      createdAt: item[0],
      slots: item[1],
    });
  });

  return finalArray;
}

export type slotStatus = "Accepted" | "Declined" | "Pending" | "Auto-Declined";

export function getSlotStatus({
  slot,
  statusOf,
}: {
  slot: IGeneralSlotResponse;
  statusOf?: EnumUserTypes;
}): slotStatus {
  const isProposedBy = slot?.proposedBy?.userType;

  if (isProposedBy === EnumUserTypes.RECRUITER) {
    if (
      (!slot?.isCompanyAvailable &&
        slot?.isCompanyAvailable === false &&
        statusOf === EnumUserTypes.HIRING_COMPANY) ||
      (!slot?.isCandidateAvailable &&
        slot?.isCandidateAvailable === false &&
        statusOf === EnumUserTypes.CANDIDATE)
    ) {
      return "Declined";
    } else if (
      (slot?.isCompanyAvailable &&
        slot?.isCompanyAvailable === true &&
        statusOf === EnumUserTypes.HIRING_COMPANY) ||
      (slot?.isCandidateAvailable &&
        slot?.isCandidateAvailable === true &&
        statusOf === EnumUserTypes.CANDIDATE)
    ) {
      return "Accepted";
    } else if (
      ((slot?.isRecruiterAvailable === false ||
        slot?.isCandidateAvailable === false) &&
        slot?.isCompanyAvailable !== false &&
        statusOf === EnumUserTypes.HIRING_COMPANY) ||
      ((slot?.isRecruiterAvailable === false ||
        slot?.isCompanyAvailable === false) &&
        slot?.isCandidateAvailable !== false &&
        statusOf === EnumUserTypes.CANDIDATE)
    ) {
      return "Auto-Declined";
    } else {
      return "Pending";
    }
  }

  if (isProposedBy === EnumUserTypes.CANDIDATE) {
    if (
      (!slot?.isCompanyAvailable &&
        slot?.isCompanyAvailable === false &&
        statusOf === EnumUserTypes.HIRING_COMPANY) ||
      (!slot?.isRecruiterAvailable &&
        slot?.isRecruiterAvailable === false &&
        statusOf === EnumUserTypes.RECRUITER)
    ) {
      return "Declined";
    } else if (
      (slot?.isCompanyAvailable &&
        slot?.isCompanyAvailable === true &&
        statusOf === EnumUserTypes.HIRING_COMPANY) ||
      (slot?.isRecruiterAvailable &&
        slot?.isRecruiterAvailable === true &&
        statusOf === EnumUserTypes.RECRUITER)
    ) {
      return "Accepted";
    } else if (
      ((slot?.isRecruiterAvailable === false ||
        slot?.isCandidateAvailable === false) &&
        slot?.isCompanyAvailable !== false &&
        statusOf === EnumUserTypes.HIRING_COMPANY) ||
      ((slot?.isCandidateAvailable === false ||
        slot?.isCompanyAvailable === false) &&
        slot?.isRecruiterAvailable !== false &&
        statusOf === EnumUserTypes.RECRUITER)
    ) {
      return "Auto-Declined";
    } else {
      return "Pending";
    }
  }

  if (isProposedBy === EnumUserTypes.HIRING_COMPANY) {
    if (
      (!slot?.isCandidateAvailable &&
        slot?.isCandidateAvailable === false &&
        statusOf === EnumUserTypes.CANDIDATE) ||
      (!slot?.isRecruiterAvailable &&
        slot?.isRecruiterAvailable === false &&
        statusOf === EnumUserTypes.RECRUITER)
    ) {
      return "Declined";
    } else if (
      (slot?.isCandidateAvailable &&
        slot?.isCandidateAvailable === true &&
        statusOf === EnumUserTypes.CANDIDATE) ||
      (slot?.isRecruiterAvailable &&
        slot?.isRecruiterAvailable === true &&
        statusOf === EnumUserTypes.RECRUITER)
    ) {
      return "Accepted";
    } else if (
      ((slot?.isRecruiterAvailable === false ||
        slot?.isCompanyAvailable === false) &&
        slot?.isRecruiterAvailable !== false &&
        statusOf === EnumUserTypes.RECRUITER) ||
      ((slot?.isRecruiterAvailable === false ||
        slot?.isCompanyAvailable === false) &&
        slot?.isCandidateAvailable !== false &&
        statusOf === EnumUserTypes.CANDIDATE)
    ) {
      return "Auto-Declined";
    } else {
      return "Pending";
    }
  }
}

export function extractProposalGroupAndAvailabilities(
  generalAvailabilitySlots: any
) {
  // Grouping availabilities from recent to oldest
  const proposalGroup = separatePastAndNewProposals(generalAvailabilitySlots);
  if (proposalGroup?.length) {
    const formattedAvailabilitySlots: IAvailabilitySlot[] =
      proposalGroup[0]?.slots?.map((slot) => {
        const slotStatus = getSlotStatus({
          slot,
          statusOf: store.getState().auth?.user?.userType,
        });
        const tailoredSlot = { ...slot };
        if (slotStatus === "Pending") {
          tailoredSlot.isAccepted = false;
          tailoredSlot.isDeclined = false;
          tailoredSlot.isAcceptedTemp = false;
          tailoredSlot.isDeclinedTemp = false;
        } else if (slotStatus === "Accepted") {
          tailoredSlot.isAccepted = true;
          tailoredSlot.isDeclined = false;
          tailoredSlot.isAcceptedTemp = true;
          tailoredSlot.isDeclinedTemp = false;
        } else if (slotStatus === "Declined") {
          tailoredSlot.isDeclined = true;
          tailoredSlot.isAccepted = false;
          tailoredSlot.isDeclinedTemp = true;
          tailoredSlot.isAcceptedTemp = false;
        }
        return tailoredSlot;
      });
    return { proposalGroup, formattedAvailabilitySlots };
    // availabilitySlotsRef.current = formattedAvailabilitySlots;
    // setAvailabilitySlots(formattedAvailabilitySlots);
  }
}

const MeetingPollDetailPage = () => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const availabilitySlotsRef = useRef<IAvailabilitySlot[]>([]);
  const { pathname } = useLocation();
  const { user } = useSelector((state: RootState) => state.auth);
  const [searchParams, setSearchParams] = useSearchParams();
  const interviewId = searchParams.get("id");

  // Local states
  const [interviewDetails, setInterviewDetails] =
    useState<IGetInterviewByIdResponse>();
  const [isFetchingInterviewDetails, setIsFetchingInterviewDetails] =
    useState(true);
  const [availabilitySlots, setAvailabilitySlots] = useState<
    IAvailabilitySlot[]
  >([]);

  const [groupOfProposals, setGroupOfProposals] = useState<
    TypeGroupOfProposals[]
  >([]);

  const fetchInterviewDetails = async () => {
    try {
      setIsFetchingInterviewDetails(true);
      const { success, data } = await getInterviewDetails(interviewId);
      if (success) {
        if (data?.slotSelectionType === "general") {
          const { proposalGroup, formattedAvailabilitySlots } =
            extractProposalGroupAndAvailabilities(
              data?.generalAvailabilitySlots
            );
          availabilitySlotsRef.current = formattedAvailabilitySlots;
          setAvailabilitySlots(formattedAvailabilitySlots);

          setGroupOfProposals(proposalGroup);
        } else if (data?.slotSelectionType === "specific") {
        }
        setInterviewDetails(data);
      }
    } catch (error) {
      console.error("Error: fetching interview details", error);
    } finally {
      setIsFetchingInterviewDetails(false);
    }
  };

  const handleCounterProposalPoll = () => {
    if (isEverySlotsDeclined) {
      navigate(`/counter-proposal-poll?id=${interviewId}`);
    } else {
      enqueueSnackbar(
        "You must decline all the availabilities to propose new time slots.",
        { variant: "error" }
      );
    }
  };

  const isHiringCompanyInvolved = useMemo(
    () => !!interviewDetails?.hiringCompanyId,
    [interviewDetails?.hiringCompanyId]
  );

  const isLatestProposalByMe = useMemo(() => {
    const latestProposal = groupOfProposals[0]?.slots[0];
    return (
      latestProposal?.proposedBy?._id ===
      (user?.userType === userTypes.RECRUITER ||
      user?.userType === userTypes.HIRING_COMPANY
        ? user?._id
        : user?.userId)
    );
  }, [groupOfProposals, user]);

  const meetingProposedBy = useMemo(
    () => availabilitySlots[0]?.proposedBy,
    [availabilitySlots]
  );

  const isInviteSend = useMemo(
    () => interviewDetails?.isInviteSend,
    [interviewDetails]
  );

  const isEverySlotsDeclined = useMemo(() => {
    return availabilitySlots?.every(
      (item) =>
        item?.isCandidateAvailable === false ||
        item?.isRecruiterAvailable === false ||
        item?.isCompanyAvailable === false
    );
  }, [availabilitySlots]);

  useEffect(() => {
    if (interviewId) fetchInterviewDetails();
  }, [interviewId]);

  console.log("details", availabilitySlotsRef.current, availabilitySlots);

  return (
    <div className="flex flex-col flex-1 items-center p-4 rounded-md bg-white">
      <div className="w-[75%] flex flex-col flex-1 min-h-0">
        {/* Header */}
        <div className="flex items-center justify-center relative pt-3">
          <button
            onClick={() => {
              if (interviewDetails?.slotSelectionType === "general") {
                navigate("/calendar/meeting/scheduling");
              } else {
                navigate("/calendar/meeting/schedules");
              }
            }}
            className={`flex items-center justify-center bg-white font-medium text-xs rounded-md text-light-black absolute left-0`}
          >
            <ChevronLeftIcon className={"w-4 h-4"} fill={"gray"} />
            Back to listing
          </button>
          <span className="font-semibold text-primary">
            Meeting{" "}
            {interviewDetails?.slotSelectionType === "general"
              ? "time poll"
              : "invite"}
          </span>
        </div>

        {/* Main body */}
        {isFetchingInterviewDetails ? (
          <div className="flex flex-col flex-1 items-center justify-center">
            <LoadingAnimation className="'w-6 h-6" color="#2e2e2e" />
          </div>
        ) : (
          <div className="flex flex-col flex-1 overflow-y-scroll min-h-0 no-scrollbar">
            {/* Meeting details */}
            <div className="flex flex-col gap-3.5 mt-9">
              <div className="flex items-center">
                <span className="text-xs font-semibold">Organizer :&nbsp;</span>
                <span className="text-xs font-medium text-light-black">
                  {interviewDetails?.createdBy?.firstName}{" "}
                  {interviewDetails?.createdBy?.lastName}
                </span>
              </div>
              <div className="flex items-center">
                <span className="text-xs font-semibold">
                  Meeting Title :&nbsp;
                </span>
                <span className="text-xs font-medium text-light-black">
                  {interviewDetails?.meetingTitle}
                </span>
              </div>

              <div className="flex items-center">
                <span className="text-xs font-semibold">
                  Meeting Type :&nbsp;
                </span>
                <span className="text-xs font-medium text-light-black">
                  {interviewDetails?.meetingMode}
                  {/* {MeetingModeEnum[interviewDetails?.meetingMode]} */}
                </span>
              </div>

              {MeetingModeEnum[interviewDetails?.meetingMode] ===
              MeetingModeEnum["Virtual Meeting"] ? (
                <div className="flex items-center">
                  <span className="text-xs font-semibold">
                    Meeting URL :&nbsp;
                  </span>
                  <div className="flex items-center border-[1.5px] rounded-md w-max h-8 px-1 ml-2 gap-2">
                    <LinkIcon className={"h-4 w-4"} fill={"gray"} />
                    <span className="text-xs font-medium text-light-black line-clamp-1">
                      {interviewDetails?.url}
                    </span>
                    <CopyToClipboard
                      text={interviewDetails?.url}
                      onCopy={() =>
                        enqueueSnackbar("Link copied successfully.", {
                          variant: "success",
                        })
                      }
                    >
                      <button className="h-6 w-6 rounded-sm bg-primary flex items-center justify-center">
                        <CopyIcon className={"w-3 h-3"} fill="white" />
                      </button>
                    </CopyToClipboard>
                  </div>
                </div>
              ) : MeetingModeEnum[interviewDetails?.meetingMode] ===
                MeetingModeEnum["Face-to-Face"] ? (
                <div className="flex items-center">
                  <span className="text-xs font-semibold">
                    Meeting Address :&nbsp;
                  </span>
                  <span className="text-xs font-medium text-light-black">
                    {interviewDetails?.address}
                  </span>
                </div>
              ) : MeetingModeEnum[interviewDetails?.meetingMode] ===
                MeetingModeEnum["Telephone Call"] ? (
                <div className="flex items-center">
                  <span className="text-xs font-semibold">
                    Agent will call on :&nbsp;
                  </span>
                  <span className="text-xs font-medium text-light-black">
                    {interviewDetails?.url || "+447480734898"}
                  </span>
                </div>
              ) : null}

              <div className="flex flex-col gap-2">
                <span className="text-xs font-semibold">
                  Meeting Description :&nbsp;
                </span>
                <span className="text-xs font-medium text-light-black">
                  {interviewDetails?.meetingDescription}
                </span>
              </div>
            </div>
            {/* InviteConfirmationContainer */}
            {isInviteSend ? (
              <InviteConfirmationContainer
                interviewDetails={interviewDetails}
                setInterviewDetails={setInterviewDetails}
                slot={interviewDetails?.specificSlot}
              />
            ) : null}

            {interviewDetails?.slotSelectionType === "specific" &&
            !isInviteSend ? (
              <SpecificSlotProposal
                meetingProposedBy={meetingProposedBy}
                isEverySlotsDeclined={isEverySlotsDeclined}
                interviewDetails={interviewDetails}
                setInterviewDetails={setInterviewDetails}
                availabilitySlots={availabilitySlots}
                setAvailabilitySlots={setAvailabilitySlots}
                groupOfProposals={groupOfProposals}
                isHiringCompanyInvolved={isHiringCompanyInvolved}
                availabilitySlotsRef={availabilitySlotsRef}
                isInviteSend={isInviteSend}
                hasChanges={
                  !isDeepEqual(availabilitySlotsRef.current, availabilitySlots)
                }
              />
            ) : null}

            {/* Latest Proposal */}
            {!isLatestProposalByMe &&
            !isInviteSend &&
            interviewDetails?.slotSelectionType === "general" ? (
              <LatestProposalCard
                meetingProposedBy={meetingProposedBy}
                isEverySlotsDeclined={isEverySlotsDeclined}
                interviewDetails={interviewDetails}
                setInterviewDetails={setInterviewDetails}
                availabilitySlots={availabilitySlots}
                setAvailabilitySlots={setAvailabilitySlots}
                groupOfProposals={groupOfProposals}
                isHiringCompanyInvolved={isHiringCompanyInvolved}
                availabilitySlotsRef={availabilitySlotsRef}
                isInviteSend={isInviteSend}
                hasChanges={
                  !isDeepEqual(availabilitySlotsRef.current, availabilitySlots)
                }
              />
            ) : null}

            {/* Past Proposals */}
            {groupOfProposals?.length &&
            !isInviteSend &&
            interviewDetails?.slotSelectionType === "general"
              ? groupOfProposals?.map((proposal, proposalIndex) => {
                  return proposalIndex !== 0 || isLatestProposalByMe ? (
                    <>
                      <PastProposalCard
                        key={proposal?.createdAt}
                        isEverySlotsDeclined={isEverySlotsDeclined}
                        index={proposalIndex}
                        interviewId={interviewId}
                        proposal={proposal}
                        isHiringCompanyInvolved={isHiringCompanyInvolved}
                        isLatestProposalByMe={isLatestProposalByMe}
                        isInviteSend={isInviteSend}
                      />

                      {/* Propose new time slot button */}
                      {isEverySlotsDeclined && proposalIndex === 0 ? (
                        <div className="flex flex-col items-center mt-5 gap-3">
                          <span className="text-xs font-medium text-light-black">
                            If none of the above time slots match your
                            availability, you may propose new one.
                          </span>
                          <button
                            onClick={handleCounterProposalPoll}
                            className="flex itesm-center justify-center text-white text-xs font-semibold bg-primary rounded-sm py-2 px-4 gap-1"
                          >
                            <PlusCircleIcon
                              className={"w-4 h-4"}
                              fill={"white"}
                            />
                            Propose new times.
                          </button>
                        </div>
                      ) : null}
                    </>
                  ) : null;
                })
              : null}
          </div>
        )}
      </div>
    </div>
  );
};

export default MeetingPollDetailPage;
